import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, List, ListItem, ListItemText, IconButton, Dialog, DialogTitle, DialogContent, TextField, DialogActions } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useAuth } from '../AuthContext';

const User = () => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserName, setNewUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { token } = useAuth();

  useEffect(() => {
    // Fetch all users when the component mounts
    const fetchUsers = async () => {
      try {
        const response = await fetch('https://sirius.runasp.net/api/Account/GetAllUsers', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const data = await response.json();
        setUsers(data);
      } catch (err) {
        setError('Failed to fetch users');
        console.error(err);
      }
    };

    fetchUsers();
  }, [token]);

  const handleDelete = async (userId) => {
    try {
      const response = await fetch(`https://sirius.runasp.net/api/Account`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      // Update the UI after deletion
      setUsers(users.filter((user) => user.id !== userId));
    } catch (err) {
      setError('Failed to delete user');
      console.error(err);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setNewUserName(user.userName);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
    setNewUserName('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleChangeUserName = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch('https://sirius.runasp.net/api/Account/ChangeUserName', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          userName: newUserName,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to change username');
      }

      // Update the UI after username change
      setUsers(users.map((user) => (user.id === selectedUser.id ? { ...user, userName: newUserName } : user)));
      handleClose();
    } catch (err) {
      setError('Failed to change username');
      console.error(err);
    }
  };

  const handleChangePassword = async () => {
    if (!selectedUser || newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const response = await fetch('https://sirius.runasp.net/api/Account/ChangePassword', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          password: newPassword,
          confirmPassword: confirmPassword,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to change password');
      }

      handleClose();
    } catch (err) {
      setError('Failed to change password');
      console.error(err);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4">
          User Management
        </Typography>
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
        <List>
          {users.map((user) => (
            <ListItem key={user.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <ListItemText
                primary={user.userName}
                secondary={user.email}
              />
              <Box>
                <IconButton color="primary" onClick={() => handleEdit(user)}>
                  <Edit />
                </IconButton>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleDelete(user.id)}
                >
                  Delete
                </Button>
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="New Username"
            fullWidth
            value={newUserName}
            onChange={(e) => setNewUserName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Confirm Password"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleChangeUserName} color="primary">
            Change Username
          </Button>
          <Button onClick={handleChangePassword} color="primary">
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default User;
